import type { Conversation, ConversationReport, Visit } from "./types";

export function createVisitFromConversation(conversation: Conversation): Visit {
  return {
    id: `visit-${conversation.id}-${Date.now().toString(36)}`,
    conversationId: conversation.id,
    customer: conversation.customer,
    site: conversation.site,
    title: `Visita · ${conversation.customer} · ${conversation.site}`,
    assignedTo: conversation.assignedTo,
    status: "draft",
    objective:
      conversation.commitments.find((item) => /visita|revis|inspecci|diagn[oó]st/i.test(item.description))
        ?.description ?? conversation.summary,
    nextSteps: conversation.commitments
      .filter((item) => item.status === "pending")
      .map((item) => item.description)
      .slice(0, 6),
    createdAt: new Date().toISOString(),
  };
}

export function createReportFromConversation(conversation: Conversation): ConversationReport {
  const findings = conversation.tags.map((tag) => `Tema identificado: ${tag}.`);
  if (conversation.visitRequired) findings.push("La conversación sugiere coordinación o ejecución de una visita.");
  if (conversation.reportRequired) findings.push("El caso requiere cotización, diagnóstico o reporte posterior.");
  return {
    id: `report-${conversation.id}-${Date.now().toString(36)}`,
    conversationId: conversation.id,
    title: `Reporte de conversación · ${conversation.customer}`,
    customer: conversation.customer,
    site: conversation.site,
    createdAt: new Date().toISOString(),
    summary: conversation.summary,
    findings: findings.length ? findings : ["Conversación pendiente de clasificación operacional."],
    commitments: conversation.commitments,
    sourceMessageIds: [...new Set(conversation.commitments.map((item) => item.sourceMessageId))],
    status: "draft",
  };
}

export function reportDocument(report: ConversationReport, conversation?: Conversation) {
  const sourceMessages = conversation
    ? report.sourceMessageIds
        .map((id) => conversation.messages.find((message) => message.id === id))
        .filter(Boolean)
    : [];
  const date = new Intl.DateTimeFormat("es-CL", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(report.createdAt));

  return `<!doctype html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(report.title)}</title>
  <style>
    :root{font-family:Inter,system-ui,sans-serif;color:#24313d;background:#fff}*{box-sizing:border-box}body{margin:0;padding:42px}main{max-width:900px;margin:auto}.brand{display:flex;justify-content:space-between;gap:24px;padding-bottom:22px;border-bottom:2px solid #17324d}.brand b{font:700 20px Manrope,system-ui;color:#17324d}.brand span{font-size:12px;color:#6d7a84}.eyebrow{font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#2f7e78}h1{font:700 32px/1.1 Manrope,system-ui;color:#17324d;margin:18px 0 8px}h2{font:700 18px Manrope,system-ui;color:#17324d;margin:0 0 12px}p,li{font-size:13px;line-height:1.6}.meta{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:24px 0}.meta div,.card{border:1px solid #dfe7e2;border-radius:12px;padding:14px;background:#f8faf8}.meta span{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#78857d}.meta b{display:block;margin-top:5px;font-size:12px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-top:16px}.card{background:#fff}.card ul{margin:0;padding-left:18px}.tasks{width:100%;border-collapse:collapse;font-size:12px}.tasks th,.tasks td{padding:10px;border-bottom:1px solid #e5ebe7;text-align:left;vertical-align:top}.tasks th{font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#6d7a84}.source{margin-top:8px;padding:10px 12px;border-left:3px solid #2f7e78;background:#f3f8f6}.source small{display:block;margin-bottom:4px;color:#6d7a84}.footer{margin-top:32px;padding-top:16px;border-top:1px solid #dfe7e2;font-size:10px;color:#78857d}@media print{body{padding:0}.no-print{display:none}}@media(max-width:700px){body{padding:20px}.meta,.grid{grid-template-columns:1fr}}
  </style>
</head>
<body>
<main>
  <header class="brand"><div><b>Conversa Smart</b><span>Reporte verificable de conversación</span></div><span>${escapeHtml(date)}</span></header>
  <p class="eyebrow">BORRADOR PARA REVISIÓN</p>
  <h1>${escapeHtml(report.title)}</h1>
  <p>${escapeHtml(report.summary)}</p>
  <section class="meta">
    <div><span>Cliente</span><b>${escapeHtml(report.customer)}</b></div>
    <div><span>Instalación</span><b>${escapeHtml(report.site)}</b></div>
    <div><span>Estado</span><b>${report.status === "approved" ? "Aprobado" : "Borrador"}</b></div>
  </section>
  <section class="grid">
    <article class="card"><h2>Hallazgos</h2><ul>${report.findings.map((item) => `<li>${escapeHtml(item)}</li>`).join("")}</ul></article>
    <article class="card"><h2>Control de evidencia</h2><p>${report.sourceMessageIds.length} mensaje(s) fuente vinculados a compromisos.</p><p>La información debe ser validada por el responsable antes de enviarse al cliente.</p></article>
  </section>
  <section class="card" style="margin-top:16px"><h2>Compromisos y próximas acciones</h2><table class="tasks"><thead><tr><th>Acción</th><th>Responsable</th><th>Plazo</th><th>Estado</th></tr></thead><tbody>${report.commitments
    .map(
      (item) =>
        `<tr><td>${escapeHtml(item.description)}</td><td>${escapeHtml(item.responsible)}</td><td>${escapeHtml(item.dueAt ?? "Por definir")}</td><td>${item.status === "done" ? "Completado" : "Pendiente"}</td></tr>`,
    )
    .join("")}</tbody></table></section>
  ${sourceMessages.length ? `<section class="card" style="margin-top:16px"><h2>Mensajes de respaldo</h2>${sourceMessages
    .map(
      (message) =>
        `<blockquote class="source"><small>${escapeHtml(message!.sender)} · ${escapeHtml(message!.sentAt)}</small>${escapeHtml(message!.body)}</blockquote>`,
    )
    .join("")}</section>` : ""}
  <footer class="footer">Generado por Conversa Smart. Este documento es un borrador operacional y no reemplaza la revisión humana.</footer>
</main>
</body>
</html>`;
}

export function printReport(report: ConversationReport, conversation?: Conversation) {
  const popup = window.open("", "_blank", "noopener,noreferrer");
  if (!popup) throw new Error("POPUP_BLOCKED");
  popup.document.open();
  popup.document.write(reportDocument(report, conversation));
  popup.document.close();
  popup.focus();
  window.setTimeout(() => popup.print(), 250);
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
